import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Field, Formik } from 'formik';
import Select from '../Forms/FormSelect';
import { Button, Modal } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
    getAllSafetyMeetingService,
    updateSafetyMeetingsService,
} from '../../../utils/apiServices/safetyServiceAPI';
import { popup } from '../../../utils';
import { QUERY_KEYS } from '../../../constants';
import { CircleSpinner } from '../../../assets/svg';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const ReplaceSafetyMeetingModal = ({ showModal, setShowModal, id, fileName }) => {
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const {
        data: safetyMeetings,
        isFetching,
        isError,
        error,
    } = useQuery({
        queryKey: [QUERY_KEYS.SAFETY_MEETING, debouncedSearch, '25'],
        queryFn: async () =>
            await getAllSafetyMeetingService({
                search: debouncedSearch,
                offset: 1,
                limit: 25,
            }),
        select: (data) =>
            data.data.map((sf) => ({
                value: sf.s3_key,
                label: sf.file_name,
            })),
        enabled: showModal,
    });
    if (isError) popup('error', error.message);

    return (
        <>
            <Modal
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                }}
                dialogClassName='overflow-hidden'
                centered
                key={id}
            >
                <Modal.Header closeButton>
                    <Modal.Title
                        className='me-2'
                        style={{ fontSize: '18px' }}
                    >{`Replace ${fileName}`}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Formik
                        initialValues={{
                            s3_key: '',
                        }}
                        onSubmit={async ({ s3_key }, { setSubmitting }) => {
                            try {
                                setSubmitting(true);
                                await updateSafetyMeetingsService({
                                    data: {
                                        s3_key: s3_key?.value,
                                    },
                                    id,
                                });
                                setShowModal(false);
                                popup(
                                    'success',
                                    `${fileName}'s link replaced with ${s3_key?.label}`,
                                );
                                queryClient.invalidateQueries({
                                    queryKey: [QUERY_KEYS.SAFETY_MEETING],
                                    exact: false,
                                });
                            } catch (error) {
                                popup('error', error.message);
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                    >
                        {({ values, isSubmitting, handleSubmit }) => (
                            <form action=''>
                                <div className='w-100'>
                                    <div className='d-flex justify-content-between'>
                                        <Field
                                            name='s3_key'
                                            label='Safety Files'
                                            options={safetyMeetings}
                                            placeholder='Select a file...'
                                            component={Select}
                                            width={'100%'}
                                            isClearable={!!values.s3_key}
                                            onInputChange={setSearch}
                                            isSearchable
                                            isLoading={isFetching}
                                            col={12}
                                        />
                                    </div>
                                    <div className='divider mt-2 mb-3'></div>
                                    <div className='d-flex w-100 align-items-center justify-content-end modal-btn'>
                                        <Button
                                            type='submit'
                                            onClick={handleSubmit}
                                            className='table-footer-btn'
                                            disabled={!values.s3_key || isSubmitting}
                                        >
                                            {isSubmitting && (
                                                <span className='me-1'>
                                                    <CircleSpinner />
                                                </span>
                                            )}
                                            Replace
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>
        </>
    );
};

ReplaceSafetyMeetingModal.propTypes = {
    id: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    setShowModal: PropTypes.func.isRequired,
    debouncedApiCall: PropTypes.func,
    showModal: PropTypes.bool.isRequired,
};
